import { isMobileOnly } from 'react-device-detect';
import { createBrowserHistory } from 'history';
import { GIFT_CARD_ERRORS } from './consts';
import { IErrorsProps, IExtensionProps } from '../models/common-models';

export const getUrlParam = (key: string) => {
  const searchParams = typeof window !== 'undefined' ? window.location?.search : '';
  const urlParams = new URLSearchParams(searchParams);
  return urlParams.get(key);
};

export const updateUrlParam = (
  params: { key: string; newValue: string | null }[],
  newPathname?: string
) => {
  if (!params || !params.length) {
    return;
  }
  const { href } = window.location;
  const urlObj = new URL(href);
  const { pathname, searchParams } = urlObj;
  const newParams = new URLSearchParams(searchParams);
  params.forEach((param) => {
    if (param?.newValue) {
      newParams.set(param.key, param.newValue.toString());
    } else {
      newParams.delete(param.key);
    }
  });
  const search = newParams.toString();
  const history = createBrowserHistory();
  if (newPathname) {
    window.location.assign(`${window.location.origin}${newPathname}?${search}`);
  } else {
    history.push(`${window.location.origin}${pathname}?${search}`);
  }
};

export const getTmxId = (): string => {
  let nativeTMXID: string | null = '';
  if (window.__EXPERIENCE_CONTEXT__?.isConsumerApp) {
    nativeTMXID = getUrlParam('guid');
  }
  return nativeTMXID || window.THD_ACCOUNT_LIB?.TMX?.getTMXsessionId();
};

export const getChannelID = (): number => (isMobileOnly ? 2 : 1);

export const getCustomerInfo = () => {
  let customerAccountId: string | null = '';
  let userId: string | null = '';
  let jwt: string | null = '';
  let role: string | null = '';
  let userEmail: string | null = '';

  if (typeof window !== 'undefined' && window.THDCustomer?.default) {
    customerAccountId = window.THDCustomer?.default.svocID;
    userId = window.THDCustomer?.default.userID;
    jwt = window.THDCustomer?.default?.jwt;
    role = window.THDCustomer?.default?.role;
    userEmail = window.THDCustomer?.default?.email;
  }
  return {
    customerAccountId, userId, jwt, role, userEmail
  };
};

export const isInActive = (
  isLegacy: boolean | undefined,
  onlineTransactable: boolean | undefined
) => {
  return !isLegacy && !onlineTransactable;
};

export const scrollToPageTop = () => {
  const anchor = document.querySelector('#savedGiftCardsHeader');
  if (anchor) {
    const rect = anchor.getBoundingClientRect();
    const isInViewport = rect.top >= 0
      && rect.left >= 0
      && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
      && rect.right <= (window.innerWidth || document.documentElement.clientWidth);
    if (!isInViewport) {
      anchor.scrollIntoView({ behavior: 'smooth' });
    }
  }
};

export const getImageName = (imageFileName: string) => {
  const fileWithoutExtension = imageFileName.split('.')[0];
  const fileNameWithoutPrefix = fileWithoutExtension.slice(9);
  const fileNameWithoutSpecialChar = fileNameWithoutPrefix.replace(/_/g, ' ');
  return fileNameWithoutSpecialChar;
};

export const getIsReCaptchaError = (errors: IErrorsProps) => {
  if (!errors || !errors?.graphQLErrors?.length) return false;

  try {
    return (
      errors?.graphQLErrors[0]?.extensions?.errors[0]?.errorCode
      === GIFT_CARD_ERRORS.BALANCE_RECAPTCHA
    );
  } catch (_err) {
    return false;
  }
};

export const isAuthError = (errorExtensions: IExtensionProps) => {
  if (!errorExtensions || !errorExtensions?.debugInfo) {
    return false;
  }
  return errorExtensions.debugInfo?.httpStatus === GIFT_CARD_ERRORS.UNAUTH;
};

export const redirectToLogin = () => {
  // eslint-disable-next-line max-len
  window.location.href = `${window.location.origin}/auth/view/signin?redirect=/myaccount/payments?selectedTab=giftCards`;
};

export const formatDollar = (value: number) => {
  const absoluteValue = Math.abs(value);
  const formattedAmount = parseFloat(absoluteValue.toFixed(2));
  let stringAmount = formattedAmount.toLocaleString();
  if (stringAmount.indexOf('.') === -1) {
    stringAmount += '.00';
  } else if (/\.\d$/.test(stringAmount)) {
    stringAmount += '0';
  }
  return '$' + stringAmount;
};

export const formatMaskedCardNumber = (cardNumberLast4: string) => {
  return `**** ***** **${cardNumberLast4}*******`;
};

export const getCurrentStoreId = (): string | null => {
  try {
    if (typeof window !== 'undefined' && window.THD_LOCALIZER_AUTO_INIT?.Localizer) {
      return window.THD_LOCALIZER_AUTO_INIT.Localizer.getLocalizedStore()?.storeId || null;
    }
    return null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to get current store ID:', error);
    return null;
  }
};

export const isStoreIdInList = (
  storeIdList: string | number | null | undefined
): boolean => {
  if (storeIdList === null || storeIdList === undefined) return false;

  // If a number is passed, compare it to the current store id stringified
  if (typeof storeIdList === 'number') {
    const currentStoreId = getCurrentStoreId();
    if (!currentStoreId) return false;
    return String(storeIdList) === currentStoreId;
  }

  // At this point we expect a string
  const normalized = storeIdList.trim();
  // Handle special feature-switch values
  if (normalized.toUpperCase() === 'ALL_STORES') return true;
  if (normalized.toUpperCase() === 'NO_STORES') return false;

  const currentStoreId = getCurrentStoreId();
  if (!currentStoreId || normalized.length === 0) {
    return false;
  }

  const storeIds = normalized.split(',').map((id) => id.trim());
  return storeIds.includes(currentStoreId);
};
